import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Location } from '@angular/common';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { guiActions } from '@app/store/gui/actions/gui.actions';
import { UserFacade } from '@app/store/user';
import { AuthService } from '../../auth/service/auth.service';
import { SKIP_REFRESH_TOKEN } from '../http-context';

const SKIP_REDIRECT_ON_PATH = ['/login/reset-password', '/login/password-reset'];

let inflightAuthRequest: Observable<Request> | null = null;

/**
 * Call refresh-token request on a 401 and replay the original request.
 * Concurrent 401s share a single refresh observable so only one refresh runs.
 */
export const apiTokenRefreshInterceptor: HttpInterceptorFn = (request, next) => {
    if (request.context.get(SKIP_REFRESH_TOKEN)) {
        return next(request);
    }
    const authService = inject(AuthService);
    const store = inject(Store);
    const userFacade = inject(UserFacade);
    const router = inject(Router);
    const location = inject(Location);
    const translateService = inject(TranslateService);

    return next(request).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status !== 401) {
                return throwError(() => error);
            }
            if (!inflightAuthRequest) {
                inflightAuthRequest = authService.refresh();
            }
            return inflightAuthRequest.pipe(
                switchMap(() => {
                    inflightAuthRequest = null;
                    return next(request.clone());
                }),
                catchError((retryError: HttpErrorResponse) => {
                    const hadInflight = inflightAuthRequest !== null;
                    inflightAuthRequest = null;

                    if (retryError.status === 401 && hadInflight && userFacade.state.loaded === true) {
                        store.dispatch(
                            guiActions.showErrorToast({
                                options: {
                                    summary: <string>translateService.instant('GUI.ERRORS.session_expired'),
                                },
                            }),
                        );
                    }

                    if (retryError.status === 401) {
                        userFacade.clearState();
                        const path = location.path().split('?')[0];
                        if (!SKIP_REDIRECT_ON_PATH.includes(path)) {
                            void router.navigate(['/login', 'sign-in']);
                        }
                    }

                    return throwError(() => retryError);
                }),
            );
        }),
    );
};
